// ── Caret position for a contenteditable field, as a plain integer ─────
//
// The composer field (coach-tasks.js, .ctk-quick-field) is a
// contenteditable="plaintext-only" div whose innerHTML is the highlighted-
// spans markup from highlight-spans.js, re-rendered whenever the span set
// changes. A contenteditable has no `.selectionStart`/`.setSelectionRange`
// (that's an <input>/<textarea> API) — the browser's caret lives in a
// Selection/Range pointing at a specific text NODE + offset, which shifts
// every time the DOM underneath is rebuilt. The rest of the composer
// (coach-tasks.js) needs to reason about the caret the same way it always
// did: as a single character offset into the sentence (root.textContent).
// These two functions are the only place that translation happens.
//
// Split in two on purpose:
//  - caretOffset() reads the LIVE selection via the Range API. That API
//    only exists on a real document, so this half can't be unit-tested
//    without jsdom (not configured for this repo's vitest — see
//    vitest.config.ts, `environment: 'node'`).
//  - findOffsetNode() is the part setCaretOffset() uses to turn an integer
//    offset back into a (node, offset) pair — plain depth-first recursion
//    over {nodeType, textContent, childNodes}. It takes no DOM API the
//    browser doesn't also expose on real nodes, so it is exported
//    separately and exercised directly in caret.test.js against a fake
//    node tree — no jsdom needed.

const TEXT_NODE = 3;

// Depth-first walk to the text node (+ intra-node offset) holding
// character `n` of root's flattened text (root.textContent). Clamps to the
// end of the last text node when `n` overruns the content — e.g. a caret
// offset saved just before the field's HTML shrank (a span got un-set,
// text got shorter). Returns null only when root has no text nodes at all
// (a genuinely empty field).
export function findOffsetNode(root, n) {
  let remaining = Math.max(0, n);
  let last = null;

  function walk(node) {
    if (node.nodeType === TEXT_NODE) {
      const len = (node.textContent || '').length;
      last = node;
      if (remaining <= len) return { node, offset: remaining };
      remaining -= len;
      return null;
    }
    const kids = node.childNodes || [];
    for (let i = 0; i < kids.length; i++) {
      const found = walk(kids[i]);
      if (found) return found;
    }
    return null;
  }

  const hit = walk(root);
  if (hit) return hit;
  if (last) return { node: last, offset: (last.textContent || '').length };
  return null;
}

// The caret's current position inside `root`, as a plain character offset
// into root.textContent. 0 if there's no live selection inside root (field
// not focused, or nothing to report yet).
export function caretOffset(root) {
  const sel = typeof window !== 'undefined' && window.getSelection ? window.getSelection() : null;
  if (!sel || sel.rangeCount === 0) return 0;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.endContainer)) return 0;
  // Measuring the length of a Range from root's start to the caret is the
  // standard DOM trick for "character offset" — it walks exactly the same
  // text nodes findOffsetNode() would, just via the browser's own Range
  // machinery instead of a hand-rolled walk.
  const pre = range.cloneRange();
  pre.selectNodeContents(root);
  pre.setEnd(range.endContainer, range.endOffset);
  return pre.toString().length;
}

// Place the caret at character offset `n` of root's flattened text.
export function setCaretOffset(root, n) {
  const sel = typeof window !== 'undefined' && window.getSelection ? window.getSelection() : null;
  if (!sel) return;
  const hit = findOffsetNode(root, n);
  const range = document.createRange();
  if (hit) range.setStart(hit.node, hit.offset);
  else range.setStart(root, 0); // genuinely empty field — nothing to walk to
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}
